import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useTemplate } from '@/contexts/TemplateContext'
import type { PublicProfile } from '@/types'
import { BookingModal } from '@/components/booking/BookingModal'
import { Gallery } from '@/components/gallery'

interface BeautyTemplateProps {
    profile: PublicProfile
}

export function BeautyTemplate({ profile }: BeautyTemplateProps) {
    const { t } = useTranslation('public')
    const { colors, typography } = useTemplate()
    const [isBookingOpen, setIsBookingOpen] = useState(false)

    return (
        <div
            className="min-h-screen"
            style={{
                fontFamily: typography.body,
                backgroundColor: colors.colors.background,
                color: colors.colors.text,
            }}
        >
            {/* Header */}
            <header
                className="px-4 py-8 md:px-6 md:py-12 lg:px-8 text-center"
                style={{
                    backgroundColor: colors.colors.primary,
                    color: colors.colors.secondary,
                }}
            >
                {profile.profile_image ? (
                    <img
                        src={profile.profile_image}
                        alt={profile.business_name}
                        className="w-24 h-24 md:w-32 md:h-32 rounded-full border-4 mx-auto mb-4 object-cover"
                        style={{ borderColor: colors.colors.accent }}
                    />
                ) : (
                    <div
                        className="w-24 h-24 md:w-32 md:h-32 rounded-full border-4 mx-auto mb-4 flex items-center justify-center"
                        style={{ borderColor: colors.colors.accent, backgroundColor: colors.colors.secondary }}
                    >
                        <span
                            className="text-3xl md:text-4xl font-bold"
                            style={{ color: colors.colors.primary }}
                        >
                            {profile.business_name[0]}
                        </span>
                    </div>
                )}
                <h1
                    className="text-2xl md:text-3xl lg:text-4xl font-bold mb-2"
                    style={{ fontFamily: typography.heading }}
                >
                    {profile.business_name}
                </h1>
                {profile.tagline && (
                    <p className="text-base md:text-lg opacity-90">{profile.tagline}</p>
                )}
                {profile.location && (
                    <p className="text-sm md:text-base opacity-90 mt-2">
                        📍 {profile.location}
                    </p>
                )}
            </header>

            {/* Bio */}
            {profile.bio && (
                <section className="px-4 py-6 md:px-6 md:py-8 lg:px-8 border-b" style={{ borderColor: colors.colors.secondary }}>
                    <h2
                        className="text-xl md:text-2xl font-bold mb-4"
                        style={{ fontFamily: typography.heading, color: colors.colors.primary }}
                    >
                        {t('template.beauty.about')}
                    </h2>
                    <p className="leading-relaxed" style={{ lineHeight: 1.7 }}>
                        {profile.bio}
                    </p>
                </section>
            )}

            {/* Gallery */}
            {profile.gallery && profile.gallery.length > 0 && (
                <section className="px-4 py-6 md:px-6 md:py-8 lg:px-8 border-b" style={{ borderColor: colors.colors.secondary }}>
                    <h2
                        className="text-xl md:text-2xl font-bold mb-4"
                        style={{ fontFamily: typography.heading, color: colors.colors.primary }}
                    >
                        {t('template.beauty.myWork')}
                    </h2>
                    <Gallery
                        images={profile.gallery}
                        layout="grid"
                        limit={4}
                        enableLightbox
                        aspectRatio="square"
                        gapSize={3}
                    />
                </section>
            )}

            {/* Services */}
            {profile.services && profile.services.length > 0 && (
                <section className="px-4 py-6 md:px-6 md:py-8 lg:px-8 border-b" style={{ borderColor: colors.colors.secondary }}>
                    <h2
                        className="text-xl md:text-2xl font-bold mb-4"
                        style={{ fontFamily: typography.heading, color: colors.colors.primary }}
                    >
                        {t('template.beauty.services')}
                    </h2>
                    <div className="space-y-3">
                        {profile.services.map((service) => (
                            <div
                                key={service.id}
                                className="p-4 rounded-lg"
                                style={{ backgroundColor: colors.colors.secondary }}
                            >
                                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
                                    <span
                                        className="font-bold"
                                        style={{ color: colors.colors.primary }}
                                    >
                                        {service.name}
                                    </span>
                                    {service.price_display !== 'hidden' && service.price && (
                                        <span
                                            className="text-lg font-bold"
                                            style={{ color: colors.colors.accent }}
                                        >
                                            {service.price_display === 'from' && 'From '}${service.price.toFixed(2)}
                                        </span>
                                    )}
                                </div>
                                {service.description && (
                                    <p
                                        className="text-sm mt-1"
                                        style={{ color: colors.colors.textMuted }}
                                    >
                                        {service.description}
                                    </p>
                                )}
                                {service.duration_minutes && (
                                    <span
                                        className="text-xs mt-2 inline-block"
                                        style={{ color: colors.colors.textMuted }}
                                    >
                                        ⏱ {service.duration_minutes} min
                                    </span>
                                )}
                            </div>
                        ))}
                    </div>
                </section>
            )}

            {/* Contact */}
            <section className="px-4 py-6 md:px-6 md:py-8 lg:px-8 border-b" style={{ borderColor: colors.colors.secondary }}>
                <h2
                    className="text-xl md:text-2xl font-bold mb-4"
                    style={{ fontFamily: typography.heading, color: colors.colors.primary }}
                >
                    {t('template.beauty.contact')}
                </h2>
                <div className="flex flex-col sm:flex-row gap-3 sm:justify-center">
                    {profile.phone && (
                        <a
                            href={`tel:${profile.phone}`}
                            className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full font-bold text-center transition-opacity hover:opacity-90"
                            style={{
                                backgroundColor: colors.colors.primary,
                                color: colors.colors.secondary,
                            }}
                        >
                            📞 {t('template.beauty.callNow')}
                        </a>
                    )}
                    {profile.email && (
                        <a
                            href={`mailto:${profile.email}`}
                            className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full font-bold text-center transition-opacity hover:opacity-90"
                            style={{
                                backgroundColor: colors.colors.secondary,
                                color: colors.colors.primary,
                            }}
                        >
                            ✉️ {t('template.beauty.emailMe')}
                        </a>
                    )}
                    {profile.services && profile.services.length > 0 && (
                        <button
                            onClick={() => setIsBookingOpen(true)}
                            className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full font-bold text-center transition-opacity hover:opacity-90"
                            style={{
                                backgroundColor: colors.colors.primary,
                                color: colors.colors.secondary,
                            }}
                        >
                            📅 {t('template.beauty.bookNow') || 'Book Now'}
                        </button>
                    )}
                </div>
            </section>

            {/* Footer */}
            <footer
                className="px-4 py-6 md:px-6 md:py-8 lg:px-8 text-center"
                style={{
                    backgroundColor: colors.colors.secondary,
                    color: colors.colors.textMuted,
                }}
            >
                <a
                    href="/"
                    className="text-sm hover:underline"
                    style={{ color: colors.colors.primary, textDecoration: 'none' }}
                >
                    {t('template.beauty.poweredBy')}
                </a>
            </footer>

            {isBookingOpen && profile.services && profile.services.length > 0 && (
                <BookingModal
                    isOpen={isBookingOpen}
                    onClose={() => setIsBookingOpen(false)}
                    profile={profile}
                    services={profile.services}
                    categories={[]}
                />
            )}
        </div>
    )
}

export default BeautyTemplate;
